import React, { useEffect, useMemo, useState } from 'react';
import { uniqueItems } from '../../utils';
import { SearchIcon } from '../../assets/icons';
import useDebounce from './useDebounce';

interface ExpandingSearchProp<T> {
  query?: string;
  data: T[];
  props: (keyof T | string)[];
  uniqueIdRef: keyof T | null;
  onSearch: (result: T[], query?: string) => void;
  className?: string;
  placeholder?: string;
  debounce?: number;
  customFindFn?: (data: T[], value: string) => T[];
}

type CompType<T> = T & { _compId: number };

const removeDuplcates = <TType,>(data: TType[]) => {
  const dataRef = data.map((x) => JSON.stringify(x));
  const result = [...new Set(dataRef)];

  return result.map((x) => JSON.parse(x));
};

export const processSearch = <TType,>(
  data: TType[],
  props: (keyof TType | string)[],
  value: string,
  uniqueIdProp: keyof TType | null | '_compId',
  customFind?: (datasource: TType[], value: string) => TType[],
) => {
  if (!data || data.length === 0) return [];
  let results: TType[] = [];
  if (value.length > 0) {
    if (customFind) {
      results = customFind(data, value);
    }
    props.forEach((prop) => {
      if (Object.keys(data[0] as Record<string, unknown>).includes(prop as string)) {
        const filterResults = [...data].filter((x) =>
          `${x[prop as keyof TType]}`.toLowerCase().includes(value.toLowerCase()),
        );
        results = [...results, ...filterResults];
      }
    });
  } else results = [...data];

  return uniqueIdProp ? uniqueItems(results, uniqueIdProp as keyof TType) : removeDuplcates(results);
};

const ExpandingSearch = <T,>({
  data,
  props,
  uniqueIdRef,
  onSearch,
  className,
  placeholder,
  query = '',
  debounce = 300,
  customFindFn,
}: ExpandingSearchProp<T>) => {
  const [inputValue, setInputValue] = useState(query);
  const debouncedFunction = useDebounce(onSearch, debounce);

  const unifiedData = useMemo(() => {
    return (data || []).map((x, i) => ({
      _compId: i,
      ...x,
    })) as CompType<T>[];
  }, [data]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedFunction(processSearch(unifiedData, props, value, uniqueIdRef || '_compId', customFindFn), value);
  };

  useEffect(() => {
    if (inputValue !== query) setInputValue(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  return (
    <div className="relative group">
      <input
        type="search"
        value={inputValue}
        placeholder={placeholder || ''}
        onChange={onInputChange}
        className={`peer cursor-pointer relative z-10 h-11 w-11 rounded-xl bg-gray-100/80 dark:bg-slate-800/80 pl-11 outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:w-full focus:max-w-[256px] focus:cursor-text focus:border-2 focus:border-orange/30 dark:focus:border-teal-500/30 focus:pl-11 focus:pr-4 focus:bg-white dark:focus:bg-slate-800 focus:shadow-lg focus:shadow-orange/10 dark:focus:shadow-teal-500/10 transition-all duration-300 ease-out ${
          className || ''
        }`.trim()}
      />
      <SearchIcon className="absolute left-3 inset-y-0 my-auto w-5 h-5 z-10 text-gray-500 dark:text-gray-400 peer-focus:text-orange dark:peer-focus:text-teal-400 transition-colors pointer-events-none" />
    </div>
  );
};

export default ExpandingSearch;
